import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Clock, Send } from "lucide-react";
import { PortalShell } from "./PortalShell";
import { portalApi, portalErr } from "./portalApi";
import "../test/testTheme.css";

const fmtClock = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

function EngineInner() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [result, setResult] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [qLeft, setQLeft] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false);

  const loadResult = useCallback(async () => {
    try {
      const { data } = await portalApi.get(`/portal/tests/${testId}/result`);
      if (data.attempt?.result) setResult(data.attempt.result);
      else setError("No submitted attempt found.");
    } catch { setError("Could not load your result."); }
  }, [testId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await portalApi.post(`/portal/tests/${testId}/start`);
        setPayload(data);
        setAnswers(data.answers || {});
        setTimeLeft(Math.max(0, Math.round(data.deadline - data.server_now)));
      } catch (err) {
        if (err?.response?.status === 402) { navigate("/portal/tests"); return; }
        // no in-progress attempt — maybe already submitted
        await loadResult();
      }
    })();
  }, [testId, navigate, loadResult]);

  // overall countdown -> auto submit at zero
  useEffect(() => {
    if (timeLeft === null || result) return undefined;
    if (timeLeft <= 0) { doSubmit(true); return undefined; }
    const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft, result]);

  // per-question countdown -> auto-advance
  const perQ = payload?.test?.per_question_seconds || 0;
  useEffect(() => {
    if (!perQ || result || !payload) return undefined;
    setQLeft(perQ);
    const t = setInterval(() => {
      setQLeft((s) => {
        if (s <= 1) {
          setIdx((cur) => Math.min(cur + 1, payload.questions.length - 1));
          return perQ;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [idx, perQ, payload, result]);

  const select = async (qid, letter) => {
    setAnswers((a) => ({ ...a, [qid]: letter }));
    try {
      await portalApi.post(`/portal/tests/${testId}/answer`, { question_id: qid, selected: letter });
    } catch (err) {
      if (err?.response?.status === 409) { doSubmit(true); }
    }
  };

  const doSubmit = async (auto = false) => {
    if (submittedRef.current) return;
    if (!auto && !window.confirm("Submit the test now? You cannot change answers afterwards.")) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      await portalApi.post(`/portal/tests/${testId}/submit`);
    } catch { /* result endpoint recovers state */ }
    await loadResult();
    setSubmitting(false);
  };

  const q = payload?.questions?.[idx];
  const answeredCount = useMemo(() => Object.keys(answers).length, [answers]);

  if (error) return <div className="tp-wrap"><p className="tp-error" data-testid="mt-engine-error">{error}</p></div>;
  if (result) return <ResultView result={result} />;
  if (!payload) return <div className="tp-wrap"><div className="tp-hero"><p className="tp-muted" data-testid="mt-loading">Loading your test…</p></div></div>;

  return (
    <div className="tp-wrap" data-testid="mt-engine">
      <div className="tp-panel" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", position: "sticky", top: 64, zIndex: 5 }}>
        <b style={{ flex: 1, minWidth: 140 }}>{payload.test.name}</b>
        <span className="tp-pill" style={{ background: timeLeft < 300 ? "#fdeaea" : "#eef1f5", color: timeLeft < 300 ? "#c5221f" : "#333" }} data-testid="mt-timer">
          <Clock size={13} style={{ verticalAlign: -2 }} /> {fmtClock(timeLeft ?? 0)}</span>
        {perQ > 0 && <span className="tp-pill" style={{ background: "#fff7e0", color: "#8a5a00" }} data-testid="mt-perq-timer">This question: {fmtClock(qLeft ?? perQ)}</span>}
        <span className="tp-note" data-testid="mt-progress">{answeredCount}/{payload.questions.length} answered</span>
        <button className="tp-btn small" onClick={() => doSubmit(false)} disabled={submitting} data-testid="mt-submit"><Send size={13} /> {submitting ? "Submitting…" : "Submit test"}</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: payload.test.navigation ? "1fr 180px" : "1fr", gap: 16, marginTop: 16 }}>
        <div className="tp-panel" data-testid="mt-question-card">
          <p className="tp-note" style={{ marginBottom: 6 }}>Question {idx + 1} of {payload.questions.length}{q.difficulty ? ` · ${q.difficulty}` : ""}</p>
          <h2 className="tp-h2" style={{ fontSize: 18, marginBottom: 12 }} data-testid="mt-question-text">{q.text}</h2>
          {q.image_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${q.image_url}`} alt="Question diagram" style={{ maxWidth: "100%", borderRadius: 10, marginBottom: 12 }} data-testid="mt-question-image" />}
          <div style={{ display: "grid", gap: 8 }}>
            {["A", "B", "C", "D"].map((letter) => (
              <button key={letter} type="button"
                className={`tp-option ${answers[q.question_id] === letter ? "selected" : ""}`}
                onClick={() => select(q.question_id, letter)}
                data-testid={`mt-option-${letter}`}>
                <b>{letter}.</b> {q.options[letter]}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "space-between" }}>
            <button className="tp-btn ghost small" disabled={idx === 0} onClick={() => setIdx(idx - 1)} data-testid="mt-prev"><ChevronLeft size={15} /> Previous</button>
            {idx < payload.questions.length - 1
              ? <button className="tp-btn ghost small" onClick={() => setIdx(idx + 1)} data-testid="mt-next">Next <ChevronRight size={15} /></button>
              : <button className="tp-btn small" onClick={() => doSubmit(false)} data-testid="mt-submit-last">Submit test <Send size={13} /></button>}
          </div>
        </div>
        {payload.test.navigation && <div className="tp-panel" data-testid="mt-palette">
          <p className="tp-note" style={{ marginBottom: 8 }}>Questions</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6 }}>
            {payload.questions.map((qq, j) => (
              <button key={qq.question_id} type="button" onClick={() => setIdx(j)}
                style={{
                  padding: "6px 0", borderRadius: 8, border: "1px solid #dfe6ef", fontSize: 12, cursor: "pointer",
                  background: j === idx ? "#f77f00" : answers[qq.question_id] ? "#d7f3e3" : "#fff",
                  color: j === idx ? "#fff" : "#333",
                }}
                data-testid={`mt-palette-${j}`}>{j + 1}</button>
            ))}
          </div>
        </div>}
      </div>
    </div>
  );
}

function ResultView({ result }) {
  const navigate = useNavigate();
  return (
    <div className="tp-wrap" data-testid="mt-result">
      <div className="tp-hero">
        <span className="tp-eyebrow">{result.test_name}</span>
        <h1 className="tp-h1" data-testid="mt-result-score">{result.score} / {result.max_score}</h1>
        <p className="tp-sub" data-testid="mt-result-stats">{result.correct} correct · {result.incorrect} incorrect · {result.unanswered} unanswered · {result.accuracy}% accuracy · {Math.round(result.time_taken_sec / 60)} min used</p>
        {result.under_review > 0 && <p className="tp-note" data-testid="mt-under-review">{result.under_review} question(s) are under admin review and were not scored.</p>}
        <button className="tp-btn" style={{ marginTop: 12 }} onClick={() => navigate("/portal/tests")} data-testid="mt-back">Back to tests</button>
      </div>
      {result.analysis.map((a, i) => (
        <div className="tp-panel" key={a.question_id} data-testid={`mt-analysis-${i}`} style={{ borderLeft: `4px solid ${a.correct ? "#12b76a" : a.selected ? "#e5484d" : "#c4cdd9"}` }}>
          <p style={{ fontWeight: 600, fontSize: 14 }}>Q{i + 1}. {a.text}</p>
          {a.image_url && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/portal/tests/qimg/${a.question_id}`} alt="diagram" style={{ maxWidth: "100%", borderRadius: 10, margin: "8px 0" }} />}
          <p className="tp-note" style={{ margin: "6px 0" }}>
            Your answer: <b style={{ color: a.correct ? "#1a7f4b" : "#c5221f" }}>{a.selected ? `${a.selected}) ${a.options[a.display_order["ABCD".indexOf(a.selected)]]}` : "—"}</b>
            {" · "}Correct: <b style={{ color: "#1a7f4b" }}>{a.correct_display}) {a.options[a.display_order["ABCD".indexOf(a.correct_display)]]}</b>
          </p>
          {(a.explanation || a.solution) && <p className="tp-note" style={{ background: "#f6f8fb", borderRadius: 8, padding: "8px 10px" }}>{a.explanation || a.solution}</p>}
        </div>
      ))}
    </div>
  );
}

export default function PortalTestEngine() {
  return <PortalShell><EngineInner /></PortalShell>;
}
